import { type ComponentProps, forwardRef, type Ref, useEffect, useState } from 'react';
import isTouchDevice from '@alltrails/core/utils/isTouchDevice';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconButton from '../IconButton';
import Tooltip from '../Tooltip';

const IconButtonWithTooltip = forwardRef(
  (
    {
      className,
      disabled,
      elevated,
      icon,
      linkInfo,
      loading,
      onBlur,
      onClick,
      onFocus,
      size = 'md',
      style,
      tabIndex,
      uniqueId,
      theme, // Do not default this since it overrides a wrapping component's theme, same as IconButton
      title,
      type = 'button',
      variant = 'secondary',
      tooltipProps = {},
      stopPropagation = false,
      ariaControls,
      ariaExpanded
    }: ComponentProps<typeof IconButton> & {
      tooltipProps?: Partial<ComponentProps<typeof Tooltip>>;
      uniqueId: string;
    },
    ref: Ref<HTMLElement>
  ) => {
    const [renderTooltip, setRenderTooltip] = useState(true);

    const buttonElement = (
      <IconButton
        {...setDenaliAttribute('IconButtonWithTooltip')}
        className={className}
        disabled={disabled}
        elevated={elevated}
        icon={icon}
        linkInfo={linkInfo}
        loading={loading}
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        size={size}
        style={style}
        tabIndex={tabIndex}
        theme={theme}
        title={renderTooltip ? '' : title}
        ariaLabel={title}
        ariaControls={ariaControls}
        ariaExpanded={ariaExpanded}
        dataTooltipId={renderTooltip ? uniqueId : undefined}
        type={type}
        variant={variant}
        stopPropagation={stopPropagation}
        ref={ref as Ref<HTMLAnchorElement>}
      />
    );

    useEffect(() => {
      setRenderTooltip(!isTouchDevice());
    }, []);

    if (renderTooltip) {
      return <Tooltip anchorElement={buttonElement} text={title} anchorHandlesFocus {...tooltipProps} uniqueId={uniqueId} />;
    }

    return buttonElement;
  }
);

export default IconButtonWithTooltip;
